import { supabase } from './supabaseClient';
import { getCleanFullName } from '../utils/authUtils';

export interface QueryConstraint {
  type: string;
  field?: string;
  op?: string;
  val?: any;
  direction?: 'asc' | 'desc';
  limitNum?: number;
  conditions?: any[];
}

export function where(field: string, op: string, val: any): QueryConstraint {
  return { type: 'where', field, op, val };
}

export function orderBy(field: string, direction: 'asc' | 'desc' = 'asc'): QueryConstraint {
  return { type: 'orderBy', field, direction };
}

export function limit(limitNum: number): QueryConstraint {
  return { type: 'limit', limitNum };
}

export function or(...conditions: any[]): QueryConstraint {
  return { type: 'or', conditions };
}

export function query(collectionRef: any, ...constraints: QueryConstraint[]) {
  return { ...collectionRef, constraints };
}

export const collection = (db: any, path: string) => ({ type: 'collection', path });

export const doc = (dbOrCollection: any, pathOrId?: string, optionalId?: string) => {
  if (dbOrCollection?.type === 'collection') {
    const collPath = dbOrCollection.path;
    const docId = pathOrId || Math.random().toString(36).substring(2, 15);
    return { type: 'doc', path: collPath, id: docId };
  }
  return { type: 'doc', path: pathOrId, id: optionalId };
};

// --- Key Mapping Helpers ---

function camelToSnake(str: string): string {
  if (str.startsWith('_') || str.includes('-')) return str;
  if (str === 'authorMemberId') return 'author_id';
  if (str === 'receiverMemberId') return 'receiver_id';
  if (str === 'chapterId') return 'chapter_id';
  if (str === 'chapterName') return 'chapter_name';
  if (str === 'creatorId') return 'creator_id';
  if (str === 'photoURL') return 'profile_photo';
  if (str === 'whatsappNumber') return 'whatsapp_number';
  if (str === 'mustChangePassword') return 'must_change_password';
  if (str === 'participantIds') return 'participant_ids';
  if (str === 'read') return 'is_read';
  if (str === 'subscriptionStartDate') return 'subscriptionStartDate';
  if (str === 'subscriptionStart') return 'subscriptionStart';
  if (str === 'subscriptionEndDate') return 'subscriptionEndDate';
  
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

function snakeToCamel(str: string): string {
  if (str.startsWith('_') || str.includes('-')) return str;
  if (str === 'author_id') return 'authorMemberId';
  if (str === 'receiver_id') return 'receiverMemberId';
  if (str === 'chapter_id') return 'chapterId';
  if (str === 'chapter_name') return 'chapterName';
  if (str === 'creator_id') return 'creatorId';
  if (str === 'profile_photo') return 'photoURL';
  if (str === 'whatsapp_number') return 'whatsappNumber';
  if (str === 'must_change_password') return 'mustChangePassword';
  if (str === 'participant_ids') return 'participantIds';
  if (str === 'is_read') return 'read';
  
  return str.replace(/(_[a-z])/g, group =>
    group.toUpperCase().replace('_', '')
  );
}

export function keysToSnake(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(keysToSnake);
  if (typeof obj === 'object') {
    if (obj instanceof Date) return obj;
    const n: any = {};
    Object.keys(obj).forEach(k => {
      n[camelToSnake(k)] = keysToSnake(obj[k]);
    });
    return n;
  }
  return obj;
}

export function keysToCamel(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(keysToCamel);
  if (typeof obj === 'object') {
    if (obj instanceof Date) return obj;
    const n: any = {};
    Object.keys(obj).forEach(k => {
      const camelKey = snakeToCamel(k);
      const val = keysToCamel(obj[k]);
      n[camelKey] = val;
      if (k !== camelKey) {
        n[k] = val;
      }
    });
    return n;
  }
  return obj;
}

export function normalizeMeetingRecord(m: any): any {
  if (!m || typeof m !== 'object') return m;
  const mNotes = m.memberNotes || m.member_notes || {};
  const counts = mNotes.__counts || {};
  const mCount = m.memberCount !== undefined ? m.memberCount : (counts.memberCount !== undefined ? counts.memberCount : mNotes.__memberCount);
  const gCount = m.guestCount !== undefined ? m.guestCount : (counts.guestCount !== undefined ? counts.guestCount : mNotes.__guestCount);
  if (mCount !== undefined) {
    m.memberCount = Number(mCount);
    m.member_count = Number(mCount);
  }
  if (gCount !== undefined) {
    m.guestCount = Number(gCount);
    m.guest_count = Number(gCount);
  }
  if (mNotes.__status) {
    m.status = String(mNotes.__status).toUpperCase();
  }
  if (mNotes.__isCompleted !== undefined) {
    m.isCompleted = Boolean(mNotes.__isCompleted);
    m.is_completed = Boolean(mNotes.__isCompleted);
  }
  if (mNotes.__isCancelled !== undefined) {
    m.isCancelled = Boolean(mNotes.__isCancelled);
    m.is_cancelled = Boolean(mNotes.__isCancelled);
  }
  if (m.status === 'CANCELLED' || m.status === 'CANCELED') {
    m.isCancelled = true;
    m.is_cancelled = true;
    m.isCompleted = false;
    m.is_completed = false;
  } else if (m.status === 'COMPLETED' || m.status === 'DONE') {
    m.isCompleted = true;
    m.is_completed = true;
    m.isCancelled = false;
    m.is_cancelled = false;
  } else if (m.status === 'UPCOMING' || m.status === 'PENDING') {
    if (mNotes.__isCompleted === false) {
      m.isCompleted = false;
      m.is_completed = false;
    }
    if (mNotes.__isCancelled === false) {
      m.isCancelled = false;
      m.is_cancelled = false;
    }
  }
  if (mNotes.__attendance && typeof mNotes.__attendance === 'object') {
    m.attendance = { ...(m.attendance || {}), ...mNotes.__attendance };
  }
  const mAmt = m.meetingAmount !== undefined ? m.meetingAmount : (m.meeting_amount !== undefined ? m.meeting_amount : (mNotes.__meetingAmount !== undefined ? mNotes.__meetingAmount : mNotes.__meeting_amount));
  if (mAmt !== undefined) {
    m.meetingAmount = Number(mAmt);
    m.meeting_amount = Number(mAmt);
  }
  const pStatus = mNotes.__paymentStatus || mNotes.__payment_status;
  if ((!m.paymentStatus || Object.keys(m.paymentStatus).length === 0) && pStatus) {
    m.paymentStatus = pStatus;
    m.payment_status = pStatus;
  } else if (pStatus) {
    m.paymentStatus = { ...(m.paymentStatus || {}), ...pStatus };
    m.payment_status = m.paymentStatus;
  }
  const pMethods = mNotes.__paymentMethods || mNotes.__payment_methods;
  if ((!m.paymentMethods || Object.keys(m.paymentMethods).length === 0) && pMethods) {
    m.paymentMethods = pMethods;
    m.payment_methods = pMethods;
  } else if (pMethods) {
    m.paymentMethods = { ...(m.paymentMethods || {}), ...pMethods };
    m.payment_methods = m.paymentMethods;
  }
  const aCollected = mNotes.__amountCollected || mNotes.__amount_collected;
  if ((!m.amountCollected || Object.keys(m.amountCollected).length === 0) && aCollected) {
    m.amountCollected = aCollected;
    m.amount_collected = aCollected;
  } else if (aCollected) {
    m.amountCollected = { ...(m.amountCollected || {}), ...aCollected };
    m.amount_collected = m.amountCollected;
  }
  m.memberNotes = mNotes;
  m.member_notes = mNotes;
  return m;
}

// --- Query Builder & Constraints ---

function applyConstraints(queryBuilder: any, constraints: QueryConstraint[], meetingIdsFiltered: string[] | null) {
  let builder = queryBuilder;
  for (const c of (constraints || [])) {
    if (c.type === 'where') {
      const snakeField = camelToSnake(c.field || '');
      if (c.field === 'participantIds' && c.op === 'array-contains') {
        if (meetingIdsFiltered) {
          const ids = meetingIdsFiltered.length > 0 ? meetingIdsFiltered : ['00000000-0000-0000-0000-000000000000'];
          builder = builder.in('id', ids);
        }
      } else {
        if (c.op === '==') builder = builder.eq(snakeField, c.val);
        else if (c.op === '!=') builder = builder.neq(snakeField, c.val);
        else if (c.op === '<') builder = builder.lt(snakeField, c.val);
        else if (c.op === '<=') builder = builder.lte(snakeField, c.val);
        else if (c.op === '>') builder = builder.gt(snakeField, c.val);
        else if (c.op === '>=') builder = builder.gte(snakeField, c.val);
        else if (c.op === 'array-contains') builder = builder.contains(snakeField, [c.val]);
      }
    } else if (c.type === 'orderBy') {
      const snakeField = camelToSnake(c.field || '');
      builder = builder.order(snakeField, { ascending: c.direction !== 'desc' });
    } else if (c.type === 'limit') {
      builder = builder.limit(c.limitNum);
    } else if (c.type === 'or') {
      const orConditions: string[] = [];
      for (const cond of (c.conditions || [])) {
        if (cond.field === 'participantIds') {
          if (meetingIdsFiltered) {
            const ids = meetingIdsFiltered.length > 0 ? meetingIdsFiltered : ['00000000-0000-0000-0000-000000000000'];
            orConditions.push(`id.in.(${ids.join(',')})`);
          }
        } else {
          const snakeF = camelToSnake(cond.field || '');
          const opStr = cond.op === '==' ? 'eq' : 'neq';
          orConditions.push(`${snakeF}.${opStr}.${cond.val}`);
        }
      }
      const orString = orConditions.join(',');
      if (orString) builder = builder.or(orString);
    }
  }
  return builder;
}

let cachedProfileSessionId: string | null = null;
let cachedProfileData: { role?: string; chapter_id?: string } | null = null;
let cachedProfileTimestamp = 0;

async function getCachedUserProfileForQuery() {
  const now = Date.now();
  if (cachedProfileData && (now - cachedProfileTimestamp < 30000)) {
    return cachedProfileData;
  }
  
  try {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const parsed = JSON.parse(savedUser);
      const prof = parsed.profile || (parsed.role ? parsed : null);
      if (prof && prof.role && (prof.chapter_id || prof.chapterId || prof.role === 'MASTER_ADMIN')) {
        const cData = {
          role: prof.role,
          chapter_id: prof.chapter_id || prof.chapterId
        };
        cachedProfileData = cData;
        cachedProfileTimestamp = now;
        return cData;
      }
    }
  } catch (e) {}

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    cachedProfileSessionId = null;
    cachedProfileData = null;
    return null;
  }

  const { data: userProfile } = await supabase
    .from('users')
    .select('role, chapter_id')
    .eq('id', session.user.id)
    .maybeSingle();

  cachedProfileSessionId = session.user.id;
  cachedProfileData = userProfile || null;
  cachedProfileTimestamp = Date.now();
  return cachedProfileData;
}

export async function getDocs(queryRef: any) {
  const collectionPath = queryRef.path;
  let finalConstraints = [...(queryRef.constraints || [])];
  let meetingIdsFiltered: string[] | null = null;
  
  let builder = supabase.from(collectionPath).select('*');
  builder = applyConstraints(builder, finalConstraints, meetingIdsFiltered);
  
  const userProfile = await getCachedUserProfileForQuery();
  if (userProfile && userProfile.role !== 'MASTER_ADMIN' && userProfile.chapter_id) {
    const userChapterId = userProfile.chapter_id;
    const hasChapterFilter = finalConstraints.some(c => c.type === 'where' && (c.field === 'chapter_id' || c.field === 'chapterId'));
    
    if (!hasChapterFilter) {
      if (collectionPath === 'users') {
        const hasSingleIdFilter = finalConstraints.some(c => c.type === 'where' && (c.field === 'id' || c.field === 'uid' || c.field === 'phone'));
        if (!hasSingleIdFilter) {
          builder = builder.eq('chapter_id', userChapterId).neq('role', 'MASTER_ADMIN');
        }
      } else if (['meetings', 'testimonials', 'guest_invitations', 'guest_registrations'].includes(collectionPath)) {
        builder = builder.eq('chapter_id', userChapterId);
      }
    }
  }

  const { data, error } = await builder;
  if (error) {
    if (collectionPath === 'future_presentations') {
      try {
        const localRaw = localStorage.getItem('ssk_future_presentations_fallback');
        const localItems: any[] = localRaw ? JSON.parse(localRaw) : [];
        const rows = localItems.map(item => keysToCamel(item));
        const docs = rows.map(r => ({
          id: r.id,
          data: () => r,
          exists: () => true
        }));
        return { docs, empty: docs.length === 0, forEach: (cb: any) => docs.forEach(cb) };
      } catch (e) {}
    }
    console.warn("getDocs notice for", collectionPath, ":", error?.message || error);
    const emptyDocs: any[] = [];
    return { docs: emptyDocs, empty: true, forEach: (cb: any) => emptyDocs.forEach(cb) };
  }
  
  let rows = (data || []).map(row => keysToCamel(row));

  if (collectionPath === 'future_presentations') {
    try {
      const localRaw = localStorage.getItem('ssk_future_presentations_fallback');
      if (localRaw) {
        const localItems: any[] = JSON.parse(localRaw);
        const existingIds = new Set(rows.map((r: any) => String(r.id)));
        localItems.forEach(item => {
          const camelItem = keysToCamel(item);
          if (!existingIds.has(String(camelItem.id))) {
            rows.push(camelItem);
          }
        });
      }
      const statusMapRaw = localStorage.getItem('ssk_future_presentations_status_map');
      if (statusMapRaw) {
        const statusMap: Record<string, string> = JSON.parse(statusMapRaw);
        rows = rows.map((r: any) => {
          const overrideStatus = statusMap[String(r.id)];
          return overrideStatus ? { ...r, status: overrideStatus } : r;
        });
      }
    } catch (e) {}
  }
  
  if (collectionPath === 'users') {
    rows = rows.map(r => {
      let photo = r.photoURL || '';
      let extraData: any = {};
      if (photo.includes('|||')) {
        const parts = photo.split('|||');
        photo = parts[0];
        try {
          extraData = JSON.parse(parts[1] || '{}');
        } catch (e) {}
      }
      const camelExtra = keysToCamel(extraData);
      return {
        ...r,
        name: getCleanFullName(r.name),
        photoURL: photo,
        ...camelExtra
      };
    });
  }

  if (collectionPath === 'notifications') {
    rows = rows.map(r => {
      let msg = r.message || '';
      let extraData: any = {};
      if (msg.includes('|||')) {
        const parts = msg.split('|||');
        msg = parts[0];
        try {
          extraData = JSON.parse(parts[1] || '{}');
        } catch(e) {}
      }
      const camelExtra = keysToCamel(extraData);
      return {
        ...r,
        message: msg,
        ...camelExtra
      };
    });
  }

  if (collectionPath === 'testimonials') {
    rows = rows.map(r => {
      let body = r.testimonial || '';
      let extraData: any = {};
      if (body.includes('|||')) {
        const parts = body.split('|||');
        body = parts[0];
        try {
          extraData = JSON.parse(parts[1] || '{}');
        } catch(e) {}
      }
      const camelExtra = keysToCamel(extraData);
      const authorId = r.authorMemberId || r.authorId || r.author_id || r.fromUserId || r.from_user_id || '';
      const receiverId = r.receiverMemberId || r.receiverId || r.receiver_id || r.toUserId || r.to_user_id || '';
      return {
        ...r,
        authorMemberId: authorId,
        receiverMemberId: receiverId,
        author_id: authorId,
        receiver_id: receiverId,
        testimonial: body,
        ...camelExtra
      };
    });
  }

  if (collectionPath === 'one_to_one_meetings' && rows.length > 0) {
    const meetingIds = rows.map(m => m.id);
    const { data: parts, error: partsErr } = await supabase
      .from('meeting_participants')
      .select('meeting_id, user_id')
      .in('meeting_id', meetingIds);
    
    const partsMap: Record<string, string[]> = {};
    if (!partsErr && parts) {
      parts.forEach(p => {
        if (!partsMap[p.meeting_id]) partsMap[p.meeting_id] = [];
        partsMap[p.meeting_id].push(p.user_id);
      });
    }

    rows.forEach(m => {
      const sender = m.senderId || m.sender_id || m.organizerId || m.organizer_id || m.creatorId;
      const receiver = m.receiverId || m.receiver_id || m.memberId || m.member_id;
      
      m.sender_id = sender;
      m.receiver_id = receiver;
      m.organizer_id = sender;
      m.member_id = receiver;
      m.creatorId = sender;
      m.date = m.scheduledDate || m.scheduled_date || m.date;
      m.time = m.meetingTime || m.meeting_time || m.time;
      m.venue = m.meetingLocation || m.meeting_location || m.venue;

      if (partsMap[m.id] && partsMap[m.id].length > 0) {
        m.participantIds = partsMap[m.id];
      } else {
        m.participantIds = [receiver].filter(Boolean);
      }
    });
  }

  if (collectionPath === 'meetings') {
    try {
      const localMeetingsRaw = localStorage.getItem('ssk_meetings_fallback');
      if (localMeetingsRaw) {
        const localMap: Record<string, any> = JSON.parse(localMeetingsRaw);
        const existingIds = new Set(rows.map((r: any) => String(r.id)));
        rows = rows.map((r: any) => {
          const patch = localMap[String(r.id)];
          return patch ? { ...r, ...keysToCamel(patch) } : r;
        });
        Object.values(localMap).forEach((item: any) => {
          if (item && item.id && !existingIds.has(String(item.id)) && item.__isNew) {
            rows.push(keysToCamel(item));
          }
        });
      }
    } catch (e) {}
  }

  if (collectionPath === 'meetings' && rows.length > 0) {
    rows.forEach(m => {
      normalizeMeetingRecord(m);
    });
  }
  
  const docs = rows.map(row => ({
    id: row.id,
    data: () => row,
    exists: () => true
  }));
  return { docs, empty: docs.length === 0, forEach: (cb: any) => docs.forEach(cb) };
}

const EXTRA_USER_FIELDS_MAP: Record<string, boolean> = {
  business_name: true,
  profession_designation: true,
  designation: true,
  address: true,
  city: true,
  state: true,
  area: true,
  pincode: true,
  bio: true,
  website: true,
  category: true,
  password_changed: true,
  push_token: true,
  subscription_status: true,
  subscription_type: true,
  renewal_requested: true,
  renewed_by: true,
  renewed_at: true,
  default_meeting_setup: true,
  defaultMeetingSetup: true,
  workspace_checklist: true,
  growth_score: true,
  wallet_balance: true,
  walletBalance: true,
  wallet_transactions: true,
  walletTransactions: true,
};

function prepareUserPayload(partialData: any, existingPhoto: string = '') {
  const cleanData = { ...partialData };
  const extraToSave: any = {};
  
  // ALWAYS extract existing extraData from existingPhoto first!
  let currentActualPhoto = '';
  if (existingPhoto && existingPhoto.includes('|||')) {
    const parts = existingPhoto.split('|||');
    currentActualPhoto = parts[0];
    try {
      Object.assign(extraToSave, JSON.parse(parts[1] || '{}'));
    } catch (e) {}
  } else {
    currentActualPhoto = existingPhoto;
  }

  let photo = cleanData.profile_photo !== undefined ? cleanData.profile_photo : currentActualPhoto;
  
  // If the passed photo happens to contain ||| (unlikely but possible), parse it too
  if (photo && photo.includes('|||')) {
    const parts = photo.split('|||');
    photo = parts[0];
    try {
      Object.assign(extraToSave, JSON.parse(parts[1] || '{}'));
    } catch (e) {}
  }

  if (cleanData.status && typeof cleanData.status === 'string') {
    const s = cleanData.status.toUpperCase();
    cleanData.status = (s === 'INACTIVE') ? 'SUSPENDED' : s;
  }
  if (cleanData.membership_status && typeof cleanData.membership_status === 'string') {
    const ms = cleanData.membership_status.toUpperCase();
    cleanData.membership_status = (ms === 'INACTIVE') ? 'SUSPENDED' : ms;
  }


  Object.keys(EXTRA_USER_FIELDS_MAP).forEach(k => {
    if (cleanData[k] !== undefined) {
      extraToSave[k] = cleanData[k];
      delete cleanData[k];
    }
  });

  if (Object.keys(extraToSave).length > 0 || photo) {
    cleanData.profile_photo = `${photo || ''}|||${JSON.stringify(extraToSave)}`;
  }

  return cleanData;
}

export async function getDoc(docRef: any) {
  const { path, id } = docRef;
  const { data, error } = await supabase.from(path).select('*').eq('id', id).maybeSingle();
  if (error || !data) return { exists: () => false, data: () => ({}), id };
  
  const camelData = keysToCamel(data);
  
  if (path === 'users') {
    let photo = camelData.photoURL || '';
    let extraData: any = {};
    if (photo.includes('|||')) {
      const parts = photo.split('|||');
      photo = parts[0];
      try {
        extraData = JSON.parse(parts[1] || '{}');
      } catch (e) {}
    }
    const camelExtra = keysToCamel(extraData);
    Object.assign(camelData, { photoURL: photo }, camelExtra);
    camelData.name = getCleanFullName(camelData.name || camelExtra.name);
    if (camelData.displayName) {
      camelData.displayName = getCleanFullName(camelData.displayName);
    }
  }
  
  if (path === 'one_to_one_meetings') {
    const { data: parts, error: partsErr } = await supabase
      .from('meeting_participants')
      .select('user_id')
      .eq('meeting_id', id);
    
    camelData.participantIds = !partsErr && parts ? parts.map(p => p.user_id) : [];
  }

  if (path === 'testimonials') {
    let body = camelData.testimonial || '';
    let extraData: any = {};
    if (body.includes('|||')) {
      const parts = body.split('|||');
      body = parts[0];
      try {
        extraData = JSON.parse(parts[1] || '{}');
      } catch (e) {}
    }
    const camelExtra = keysToCamel(extraData);
    Object.assign(camelData, { testimonial: body }, camelExtra);
  }

  if (path === 'meetings') {
    normalizeMeetingRecord(camelData);
  }
  
  return { exists: () => true, data: () => camelData, id };
}

export async function getDocFromServer(docRef: any) {
  return getDoc(docRef);
}

export async function setDoc(docRef: any, data: any, options?: any) {
  const { path, id } = docRef;
  
  if (path === 'position_history') {
    return;
  }
  
  let participantIds: string[] | undefined = undefined;
  let cleanData = { ...data };
  if (path === 'one_to_one_meetings') {
    participantIds = cleanData.participantIds;
    delete cleanData.participantIds;
  }

  if (path === 'testimonials') {
    if (cleanData.title) {
      const extra = { title: cleanData.title };
      cleanData.testimonial = (cleanData.testimonial || '') + '|||' + JSON.stringify(extra);
    }
    delete cleanData.title;
  }

  if (path === 'meetings') {
    const mCount = cleanData.memberCount !== undefined ? cleanData.memberCount : cleanData.member_count;
    const gCount = cleanData.guestCount !== undefined ? cleanData.guestCount : cleanData.guest_count;
    const existingNotes = cleanData.memberNotes || cleanData.member_notes || {};
    const mAmt = cleanData.meetingAmount !== undefined ? cleanData.meetingAmount : cleanData.meeting_amount;
    const pStatus = cleanData.paymentStatus || cleanData.payment_status;
    const pMethods = cleanData.paymentMethods || cleanData.payment_methods;
    cleanData.member_notes = {
      ...existingNotes,
      ...(mAmt !== undefined ? { __meetingAmount: Number(mAmt) } : {}),
      ...(pStatus !== undefined ? { __paymentStatus: pStatus } : {}),
      ...(pMethods !== undefined ? { __paymentMethods: pMethods } : {}),
      __counts: {
        memberCount: Number(mCount) || 0,
        guestCount: Number(gCount) || 0
      },
      __memberCount: Number(mCount) || 0,
      __guestCount: Number(gCount) || 0
    };
    delete cleanData.memberCount;
    delete cleanData.member_count;
    delete cleanData.guestCount;
    delete cleanData.guest_count;
    delete cleanData.memberNotes;

    if (cleanData.isCancelled === true || cleanData.is_cancelled === true) {
      cleanData.status = 'CANCELLED';
      cleanData.is_completed = false;
      delete cleanData.isCompleted;
    }
    delete cleanData.isCancelled;
    delete cleanData.is_cancelled;
  }
  
  if (path === 'users') {
    let existingPhoto = '';
    try {
      const { data: existingUser } = await supabase.from('users').select('profile_photo').eq('id', id).single();
      if (existingUser) {
        existingPhoto = existingUser.profile_photo || '';
      }
    } catch (e) {}
    
    const userSnakeData = keysToSnake(cleanData);
    const preparedSnake = prepareUserPayload(userSnakeData, existingPhoto);
    const { error: userError } = await supabase.from('users').upsert({ id, ...preparedSnake }, { onConflict: 'id' });
    if (userError) {
      console.error("setDoc error:", userError);
      throw new Error(userError.message || 'Database upsert failed');
    }
    return;
  }
  
  const snakeData = keysToSnake(cleanData);
  const { error } = await supabase.from(path).upsert({ id, ...snakeData }, { onConflict: 'id' });
  if (error) {
    console.error("setDoc error:", error);
    throw new Error(error.message || 'Database upsert failed');
  }
  
  if (path === 'one_to_one_meetings' && participantIds !== undefined) {
    await supabase.from('meeting_participants').delete().eq('meeting_id', id);
    if (participantIds.length > 0) {
      const inserts = participantIds.map(uid => ({ meeting_id: id, user_id: uid }));
      await supabase.from('meeting_participants').insert(inserts);
    }
  }
}

export async function addDoc(collectionRef: any, data: any) {
  const collectionPath = collectionRef.path;
  
  if (collectionPath === 'position_history') {
    return { id: Math.random().toString(36).substring(2, 15) };
  }
  
  let participantIds: string[] | undefined = undefined;
  let cleanData = { ...data };
  if (collectionPath === 'one_to_one_meetings') {
    participantIds = cleanData.participantIds || (cleanData.participantId ? [cleanData.participantId] : undefined);
    delete cleanData.participantIds;
    delete cleanData.participantId;

    const cleanOneToOnePayload = {
      title: (cleanData.title || '').trim() || '1:1 Meeting',
      creator_id: cleanData.creator_id || cleanData.creatorId || cleanData.sender_id || cleanData.senderId || cleanData.organizer_id || cleanData.organizerId || null,
      receiver_id: cleanData.receiver_id || cleanData.receiverId || cleanData.member_id || cleanData.memberId || (participantIds && participantIds[0]) || null,
      sender_id: cleanData.creator_id || cleanData.creatorId || cleanData.sender_id || cleanData.senderId || cleanData.organizer_id || cleanData.organizerId || null,
      organizer_id: cleanData.creator_id || cleanData.creatorId || cleanData.sender_id || cleanData.senderId || cleanData.organizer_id || cleanData.organizerId || null,
      member_id: cleanData.receiver_id || cleanData.receiverId || cleanData.member_id || cleanData.memberId || (participantIds && participantIds[0]) || null,
      chapter_id: cleanData.chapter_id || cleanData.chapterId || null,
      date: cleanData.date || cleanData.scheduled_date || cleanData.scheduledDate || '',
      time: cleanData.time || cleanData.scheduled_time || cleanData.scheduledTime || '',
      scheduled_date: cleanData.scheduled_date || cleanData.scheduledDate || cleanData.date || '',
      scheduled_time: cleanData.scheduled_time || cleanData.scheduledTime || cleanData.time || '',
      venue: cleanData.venue || cleanData.meeting_location || cleanData.meetingLocation || cleanData.location || '',
      meeting_location: cleanData.meeting_location || cleanData.meetingLocation || cleanData.venue || cleanData.location || '',
      meeting_type: 'one_to_one',
      notes: cleanData.notes || cleanData.description || cleanData.topics || '',
      description: cleanData.description || cleanData.notes || cleanData.topics || '',
      status: cleanData.status || 'UPCOMING',
      created_at: cleanData.created_at || cleanData.createdAt || new Date().toISOString(),
      updated_at: cleanData.updated_at || cleanData.updatedAt || new Date().toISOString()
    };

    if (cleanData.attendance) {
      (cleanOneToOnePayload as any).attendance = cleanData.attendance;
    }

    const { data: meetingResult, error: meetingError } = await supabase
      .from('one_to_one_meetings')
      .insert([cleanOneToOnePayload])
      .select()
      .single();

    if (!meetingError && meetingResult) {
      const newId = meetingResult.id;
      if (participantIds && participantIds.length > 0) {
        try {
          const inserts = participantIds.map(uid => ({ meeting_id: newId, user_id: uid }));
          await supabase.from('meeting_participants').insert(inserts);
        } catch (pErr) {
          console.warn("Participant insert notice:", pErr);
        }
      }
      return { id: newId };
    }

    console.warn("addDoc direct Supabase one_to_one_meetings insert warning, trying /api/one-to-one-meetings/create fallback:", meetingError);
    try {
      const resp = await fetch('/api/one-to-one-meetings/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanOneToOnePayload)
      });
      const json = await resp.json();
      if (json && json.data && json.data.id) {
        return { id: json.data.id };
      }
    } catch (apiErr) {
      console.warn("API fallback error for one_to_one_meetings:", apiErr);
    }

    if (meetingError) {
      throw new Error(meetingError.message || 'Failed to schedule One-to-One Meeting.');
    }

    return { id: Math.random().toString(36).substring(2, 15) };
  }

  if (collectionPath === 'users') {
    try {
      const resp = await fetch('/api/members/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanData)
      });
      const resJson = await resp.json();
      if (!resp.ok) {
        throw new Error(resJson.error || resJson.message || 'Failed to create member');
      }
      if (resJson.id || resJson.uid) {
        return { id: resJson.id || resJson.uid };
      }
    } catch (apiErr: any) {
      if (apiErr.message?.includes('already registered')) {
        throw apiErr;
      }
      console.warn("API fallback for user insert:", apiErr);
    }

    const userSnakeData = keysToSnake(cleanData);
    const preparedSnake = prepareUserPayload(userSnakeData, '');
    const { data: userResult, error: userError } = await supabase.from(collectionPath).insert(preparedSnake).select().single();
    if (userError) {
      console.error("addDoc error:", userError);
      if (userError.code === '23505' || userError.message?.toLowerCase().includes('unique') || userError.message?.toLowerCase().includes('duplicate')) {
        throw new Error("This mobile number is already registered to a member.");
      }
      throw new Error(userError.message || 'Database insert failed');
    }
    return { id: userResult?.id || Math.random().toString(36).substring(2, 15) };
  }
  
  if (collectionPath === 'notifications') {
    const extra = {
      link: cleanData.link,
      role: cleanData.role,
      related_user_id: cleanData.relatedUserId,
      title: cleanData.title
    };
    cleanData.message = (cleanData.message || '') + '|||' + JSON.stringify(extra);
    delete cleanData.link;
    delete cleanData.role;
    delete cleanData.relatedUserId;
    delete cleanData.title;
  }

  if (collectionPath === 'testimonials') {
    const authorId = cleanData.author_id || cleanData.authorId || cleanData.authorMemberId || cleanData.author_member_id || cleanData.fromUserId || cleanData.from_user_id || null;
    const receiverId = cleanData.receiver_id || cleanData.receiverId || cleanData.receiverMemberId || cleanData.receiver_member_id || cleanData.toUserId || cleanData.to_user_id || null;

    let body = cleanData.testimonial || '';
    if (cleanData.title || cleanData.referralId || cleanData.oneToOneId || cleanData.referral_id || cleanData.one_to_one_id) {
      const extra = {
        title: cleanData.title || '',
        referralId: cleanData.referralId || cleanData.referral_id || '',
        oneToOneId: cleanData.oneToOneId || cleanData.one_to_one_id || ''
      };
      body = (body || '') + '|||' + JSON.stringify(extra);
    }

    const cleanTestimonialPayload = {
      author_id: authorId,
      receiver_id: receiverId,
      chapter_id: cleanData.chapter_id || cleanData.chapterId || null,
      rating: Number(cleanData.rating || 5),
      testimonial: body,
      status: cleanData.status || 'APPROVED',
      created_at: cleanData.created_at || cleanData.createdAt || new Date().toISOString(),
      updated_at: cleanData.updated_at || cleanData.updatedAt || new Date().toISOString()
    };

    const { data: testResult, error: testError } = await supabase.from('testimonials').insert([cleanTestimonialPayload]).select().single();
    if (testError) {
      console.error("addDoc testimonials error:", testError);
      throw new Error(testError.message || 'Failed to submit testimonial.');
    }
    return { id: testResult?.id || Math.random().toString(36).substring(2, 15) };
  }

  if (collectionPath === 'thank_you_slips') {
    const cleanSlipPayload = {
      referral_id: cleanData.referral_id || cleanData.referralId || null,
      sender_id: cleanData.sender_id || cleanData.senderId || cleanData.from_user_id || cleanData.fromUserId || cleanData.submitted_by || cleanData.submittedBy || null,
      receiver_id: cleanData.receiver_id || cleanData.receiverId || cleanData.to_user_id || cleanData.toUserId || null,
      submitted_by: cleanData.submitted_by || cleanData.submittedBy || cleanData.from_user_id || cleanData.fromUserId || null,
      from_user_id: cleanData.from_user_id || cleanData.fromUserId || cleanData.submitted_by || cleanData.submittedBy || null,
      to_user_id: cleanData.to_user_id || cleanData.toUserId || null,
      customer_name: cleanData.customer_name || cleanData.customerName || '',
      business_value: Number(cleanData.business_value || cleanData.businessValue || cleanData.businessAmount || 0),
      notes: cleanData.notes || cleanData.thankYouMessage || cleanData.businessDescription || '',
      thank_you_message: cleanData.thank_you_message || cleanData.notes || cleanData.thankYouMessage || '',
      created_at: cleanData.created_at || cleanData.createdAt || new Date().toISOString()
    };

    if (cleanSlipPayload.referral_id) {
      const { data: existing } = await supabase
        .from('thank_you_slips')
        .select('id')
        .eq('referral_id', String(cleanSlipPayload.referral_id));

      if (existing && existing.length > 0) {
        throw new Error("A Thank You Slip has already been submitted for this referral.");
      }
    }

    const { data: slipResult, error: slipError } = await supabase.from('thank_you_slips').insert([cleanSlipPayload]).select().single();
    if (!slipError && slipResult) {
      return { id: slipResult.id };
    }

    if (slipError) {
      if (slipError.code === '23505' || slipError.message?.toLowerCase().includes('unique') || slipError.message?.toLowerCase().includes('duplicate')) {
        throw new Error("A Thank You Slip has already been submitted for this referral.");
      }
    }

    console.warn("addDoc direct Supabase thank_you_slips insert warning, trying /api/thank-you-slips/create fallback:", slipError);
    try {
      const resp = await fetch('/api/thank-you-slips/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanSlipPayload)
      });
      const json = await resp.json();
      if (json && json.data && json.data.id) {
        return { id: json.data.id };
      }
    } catch (apiErr) {
      console.warn("API fallback error for thank_you_slips:", apiErr);
    }
    return { id: Math.random().toString(36).substring(2, 15) };
  }

  if (collectionPath === 'meetings') {
    const mCount = cleanData.memberCount !== undefined ? cleanData.memberCount : cleanData.member_count;
    const gCount = cleanData.guestCount !== undefined ? cleanData.guestCount : cleanData.guest_count;
    const existingNotes = cleanData.memberNotes || cleanData.member_notes || {};
    const mAmt = cleanData.meetingAmount !== undefined ? cleanData.meetingAmount : cleanData.meeting_amount;
    const pStatus = cleanData.paymentStatus || cleanData.payment_status;
    const pMethods = cleanData.paymentMethods || cleanData.payment_methods;
    cleanData.member_notes = {
      ...existingNotes,
      ...(mAmt !== undefined ? { __meetingAmount: Number(mAmt) } : {}),
      ...(pStatus !== undefined ? { __paymentStatus: pStatus } : {}),
      ...(pMethods !== undefined ? { __paymentMethods: pMethods } : {}),
      __counts: {
        memberCount: Number(mCount) || 0,
        guestCount: Number(gCount) || 0
      },
      __memberCount: Number(mCount) || 0,
      __guestCount: Number(gCount) || 0
    };
    delete cleanData.memberCount;
    delete cleanData.member_count;
    delete cleanData.guestCount;
    delete cleanData.guest_count;
    delete cleanData.memberNotes;

    if (cleanData.isCancelled === true || cleanData.is_cancelled === true) {
      cleanData.status = 'CANCELLED';
      cleanData.is_completed = false;
      delete cleanData.isCompleted;
    }
    delete cleanData.isCancelled;
    delete cleanData.is_cancelled;
  }

  const snakeData = keysToSnake(cleanData);
  let { data: result, error } = await supabase.from(collectionPath).insert(snakeData).select().single();
  if (error && collectionPath === 'meetings') {
    const fallbackData = { ...snakeData };
    delete fallbackData.meeting_amount;
    delete fallbackData.payment_status;
    delete fallbackData.payment_methods;
    const retry = await supabase.from(collectionPath).insert(fallbackData).select().single();
    if (!retry.error) {
      result = retry.data;
      error = null;
    }
  }
  if (error && collectionPath === 'future_presentations') {
    const rawSt = String(snakeData.status || 'Upcoming');
    const safeDbStatus = rawSt.toUpperCase() === 'COMPLETED' ? 'Completed' : rawSt.toUpperCase() === 'CANCELLED' ? 'Cancelled' : 'Scheduled';
    const corePayload: Record<string, any> = {
      member_id: snakeData.member_id,
      presentation_date: snakeData.presentation_date,
      status: safeDbStatus,
      created_at: snakeData.created_at || new Date().toISOString()
    };
    if (snakeData.created_by) corePayload.created_by = snakeData.created_by;
    if (snakeData.presentation_details !== undefined) corePayload.presentation_details = snakeData.presentation_details;
    const retry = await supabase.from(collectionPath).insert(corePayload).select().single();
    if (!retry.error) {
      result = retry.data;
      error = null;
      if (result?.id && rawSt) {
        try {
          const statusMapRaw = localStorage.getItem('ssk_future_presentations_status_map');
          const statusMap: Record<string, string> = statusMapRaw ? JSON.parse(statusMapRaw) : {};
          statusMap[String(result.id)] = rawSt;
          localStorage.setItem('ssk_future_presentations_status_map', JSON.stringify(statusMap));
        } catch (e) {}
      }
    }
  }
  if (error) {
    if (collectionPath === 'future_presentations') {
      try {
        const fallbackId = Math.random().toString(36).substring(2, 15);
        const localRaw = localStorage.getItem('ssk_future_presentations_fallback');
        const localItems: any[] = localRaw ? JSON.parse(localRaw) : [];
        const newItem = { id: fallbackId, ...snakeData, ...cleanData };
        localItems.push(newItem);
        localStorage.setItem('ssk_future_presentations_fallback', JSON.stringify(localItems));
        return { id: fallbackId };
      } catch (e) {}
    }
    if (collectionPath === 'meetings' && (error.code === '42804' || String(error.message || '').includes('COALESCE'))) {
      try {
        const fallbackId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);
        const localMeetingsRaw = localStorage.getItem('ssk_meetings_fallback');
        const localMap: Record<string, any> = localMeetingsRaw ? JSON.parse(localMeetingsRaw) : {};
        localMap[fallbackId] = { id: fallbackId, __isNew: true, ...snakeData, ...cleanData };
        localStorage.setItem('ssk_meetings_fallback', JSON.stringify(localMap));
        return { id: fallbackId };
      } catch (e) {}
    }
    console.error("addDoc error:", error);
    throw new Error(error.message || 'Database insert failed');
  }
  
  const newId = result?.id;
  
  if (collectionPath === 'one_to_one_meetings' && participantIds !== undefined && newId) {
    await supabase.from('meeting_participants').delete().eq('meeting_id', newId);
    if (participantIds.length > 0) {
      const inserts = participantIds.map(uid => ({ meeting_id: newId, user_id: uid }));
      await supabase.from('meeting_participants').insert(inserts);
    }
  }
  
  return { id: newId || Math.random().toString(36).substring(2, 15) };
}

export async function updateDoc(docRef: any, partialData: any) {
  const { path, id } = docRef;
  
  if (path === 'position_history') {
    return;
  }
  
  let participantIds: string[] | undefined = undefined;
  let cleanData = { ...partialData };
  if (path === 'one_to_one_meetings') {
    participantIds = cleanData.participantIds || (cleanData.participantId ? [cleanData.participantId] : undefined);
    delete cleanData.participantIds;
    delete cleanData.participantId;

    const sanitizedUpdate: Record<string, any> = {
      updated_at: new Date().toISOString()
    };

    if (cleanData.title !== undefined) sanitizedUpdate.title = (cleanData.title || '').trim();
    if (cleanData.status !== undefined) sanitizedUpdate.status = cleanData.status;
    if (cleanData.attendance !== undefined) sanitizedUpdate.attendance = cleanData.attendance;
    if (cleanData.date || cleanData.scheduled_date || cleanData.scheduledDate) {
      const d = cleanData.date || cleanData.scheduled_date || cleanData.scheduledDate;
      sanitizedUpdate.date = d;
      sanitizedUpdate.scheduled_date = d;
    }
    if (cleanData.time || cleanData.scheduled_time || cleanData.scheduledTime) {
      const t = cleanData.time || cleanData.scheduled_time || cleanData.scheduledTime;
      sanitizedUpdate.time = t;
      sanitizedUpdate.scheduled_time = t;
    }
    if (cleanData.venue || cleanData.meeting_location || cleanData.meetingLocation || cleanData.location) {
      const v = cleanData.venue || cleanData.meeting_location || cleanData.meetingLocation || cleanData.location;
      sanitizedUpdate.venue = v;
      sanitizedUpdate.meeting_location = v;
    }
    if (cleanData.notes !== undefined || cleanData.description !== undefined || cleanData.topics !== undefined) {
      const n = cleanData.notes !== undefined ? cleanData.notes : (cleanData.description !== undefined ? cleanData.description : cleanData.topics);
      sanitizedUpdate.notes = n;
      sanitizedUpdate.description = n;
    }

    const { error: updateError } = await supabase.from(path).update(sanitizedUpdate).eq('id', id);
    if (updateError) {
      console.error("updateDoc one_to_one_meetings error:", updateError);
      throw new Error(updateError.message || 'Database update failed');
    }

    if (participantIds !== undefined) {
      await supabase.from('meeting_participants').delete().eq('meeting_id', id);
      if (participantIds.length > 0) {
        const inserts = participantIds.map(uid => ({ meeting_id: id, user_id: uid }));
        await supabase.from('meeting_participants').insert(inserts);
      }
    }
    return;
  }

  if (path === 'testimonials') {
    if (cleanData.title) {
      const extra = { title: cleanData.title };
      cleanData.testimonial = (cleanData.testimonial || '') + '|||' + JSON.stringify(extra);
    }
    delete cleanData.title;
  }
  
  if (path === 'users') {
    let existingPhoto = '';
    try {
      const { data: existingUser } = await supabase.from('users').select('profile_photo').eq('id', id).single();
      if (existingUser) {
        existingPhoto = existingUser.profile_photo || '';
      }
    } catch (e) {}
    
    const userSnakeData = keysToSnake(cleanData);
    const preparedSnake = prepareUserPayload(userSnakeData, existingPhoto);
    
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (token) {
      const res = await fetch('/api/users/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ uid: id, updates: preparedSnake })
      });
      const data = await res.json();
      if (!res.ok) {
        console.error("updateDoc users error:", data.error);
        throw new Error(data.error || 'Failed to update user');
      }
    } else {
      const { error: userUpdateError } = await supabase.from('users').update(preparedSnake).eq('id', id);
      if (userUpdateError) {
        console.error("updateDoc error:", userUpdateError);
        throw new Error(userUpdateError.message || 'Database update failed');
      }
    }
    return;
  }

  if (path === 'meetings') {
    let existingDbNotes: Record<string, any> = {};
    try {
      const { data: existingRow } = await supabase.from('meetings').select('member_notes').eq('id', id).maybeSingle();
      if (existingRow && existingRow.member_notes && typeof existingRow.member_notes === 'object') {
        existingDbNotes = existingRow.member_notes;
      }
    } catch (e) {}

    const mCount = cleanData.memberCount !== undefined ? cleanData.memberCount : cleanData.member_count;
    const gCount = cleanData.guestCount !== undefined ? cleanData.guestCount : cleanData.guest_count;
    const existingNotes = {
      ...existingDbNotes,
      ...(cleanData.memberNotes || cleanData.member_notes || {})
    };
    const mAmt = cleanData.meetingAmount !== undefined ? cleanData.meetingAmount : cleanData.meeting_amount;
    const pStatus = cleanData.paymentStatus || cleanData.payment_status;
    const pMethods = cleanData.paymentMethods || cleanData.payment_methods;
    const aCollected = cleanData.amountCollected || cleanData.amount_collected;
    const att = cleanData.attendance;

    if (cleanData.isCancelled === true || cleanData.is_cancelled === true || cleanData.status === 'CANCELLED') {
      cleanData.status = 'CANCELLED';
      cleanData.is_completed = false;
      delete cleanData.isCompleted;
    } else if (cleanData.isCompleted === true || cleanData.is_completed === true || cleanData.status === 'COMPLETED') {
      cleanData.status = 'COMPLETED';
      cleanData.is_completed = true;
      delete cleanData.isCompleted;
    } else if (cleanData.status === 'UPCOMING' || cleanData.status === 'PENDING' || cleanData.isCompleted === false || cleanData.is_completed === false) {
      cleanData.status = cleanData.status ? String(cleanData.status).toUpperCase() : 'UPCOMING';
      cleanData.is_completed = false;
      delete cleanData.isCompleted;
    }

    const effectiveMCount = mCount !== undefined ? Number(mCount) : (existingNotes.__counts?.memberCount ?? existingNotes.__memberCount ?? 0);
    const effectiveGCount = gCount !== undefined ? Number(gCount) : (existingNotes.__counts?.guestCount ?? existingNotes.__guestCount ?? 0);

    cleanData.member_notes = {
      ...existingNotes,
      ...(mAmt !== undefined ? { __meetingAmount: Number(mAmt) } : {}),
      ...(pStatus !== undefined ? { __paymentStatus: pStatus } : {}),
      ...(pMethods !== undefined ? { __paymentMethods: pMethods } : {}),
      ...(aCollected !== undefined ? { __amountCollected: aCollected } : {}),
      ...(att !== undefined ? { __attendance: att } : {}),
      ...(cleanData.status !== undefined ? { __status: String(cleanData.status).toUpperCase() } : {}),
      ...(cleanData.is_completed !== undefined ? { __isCompleted: Boolean(cleanData.is_completed) } : {}),
      ...(cleanData.status !== undefined ? { __isCancelled: String(cleanData.status).toUpperCase() === 'CANCELLED' } : {}),
      __counts: {
        memberCount: Number(effectiveMCount) || 0,
        guestCount: Number(effectiveGCount) || 0
      },
      __memberCount: Number(effectiveMCount) || 0,
      __guestCount: Number(effectiveGCount) || 0
    };
    delete cleanData.memberCount;
    delete cleanData.member_count;
    delete cleanData.guestCount;
    delete cleanData.guest_count;
    delete cleanData.memberNotes;
    delete cleanData.meetingAmount;
    delete cleanData.meeting_amount;
    delete cleanData.paymentStatus;
    delete cleanData.payment_status;
    delete cleanData.paymentMethods;
    delete cleanData.payment_methods;
    delete cleanData.isCancelled;
    delete cleanData.is_cancelled;
  }
  
  const snakeData = keysToSnake(cleanData);
  let { error } = await supabase.from(path).update(snakeData).eq('id', id);
  if (error && path === 'meetings') {
    const fallbackData = { ...snakeData };
    delete fallbackData.meeting_amount;
    delete fallbackData.payment_status;
    delete fallbackData.payment_methods;
    delete fallbackData.status;
    delete fallbackData.is_completed;
    delete fallbackData.attendance;
    const retry = await supabase.from(path).update(fallbackData).eq('id', id);
    if (!retry.error) {
      error = null;
    } else {
      error = retry.error;
    }
  }
  if (!error && path === 'meetings') {
    try {
      const localMeetingsRaw = localStorage.getItem('ssk_meetings_fallback');
      if (localMeetingsRaw) {
        const localMap: Record<string, any> = JSON.parse(localMeetingsRaw);
        if (localMap[String(id)]) {
          delete localMap[String(id)];
          localStorage.setItem('ssk_meetings_fallback', JSON.stringify(localMap));
        }
      }
    } catch (e) {}
  }
  if (error && path === 'future_presentations') {
    const rawSt = String(snakeData.status || 'Upcoming');
    const safeDbStatus = rawSt.toUpperCase() === 'COMPLETED' ? 'Completed' : rawSt.toUpperCase() === 'CANCELLED' ? 'Cancelled' : 'Scheduled';
    const fallbackPayload: Record<string, any> = { ...snakeData, status: safeDbStatus };
    delete fallbackPayload.member_name;
    delete fallbackPayload.chapter_id;
    const retry = await supabase.from(path).update(fallbackPayload).eq('id', id);
    if (!retry.error) {
      error = null;
      try {
        const statusMapRaw = localStorage.getItem('ssk_future_presentations_status_map');
        const statusMap: Record<string, string> = statusMapRaw ? JSON.parse(statusMapRaw) : {};
        statusMap[String(id)] = rawSt;
        localStorage.setItem('ssk_future_presentations_status_map', JSON.stringify(statusMap));
      } catch (e) {}
    }
  }
  if (error) {
    if (path === 'future_presentations') {
      try {
        if (cleanData.status) {
          const statusMapRaw = localStorage.getItem('ssk_future_presentations_status_map');
          const statusMap: Record<string, string> = statusMapRaw ? JSON.parse(statusMapRaw) : {};
          statusMap[String(id)] = String(cleanData.status);
          localStorage.setItem('ssk_future_presentations_status_map', JSON.stringify(statusMap));
        }
        const localRaw = localStorage.getItem('ssk_future_presentations_fallback');
        if (localRaw) {
          const localItems: any[] = JSON.parse(localRaw);
          const updated = localItems.map(item => String(item.id) === String(id) ? { ...item, ...snakeData, ...cleanData } : item);
          localStorage.setItem('ssk_future_presentations_fallback', JSON.stringify(updated));
        }
        return;
      } catch (e) {}
    }
    if (path === 'meetings' && (error.code === '42804' || String(error.message || '').includes('COALESCE'))) {
      try {
        const localMeetingsRaw = localStorage.getItem('ssk_meetings_fallback');
        const localMap: Record<string, any> = localMeetingsRaw ? JSON.parse(localMeetingsRaw) : {};
        localMap[String(id)] = { ...(localMap[String(id)] || {}), id, ...snakeData, ...cleanData };
        localStorage.setItem('ssk_meetings_fallback', JSON.stringify(localMap));
        return;
      } catch (e) {}
    }
    console.error("updateDoc error:", error);
    throw new Error(error.message || 'Database update failed');
  }
  
  if (path === 'one_to_one_meetings' && participantIds !== undefined) {
    await supabase.from('meeting_participants').delete().eq('meeting_id', id);
    if (participantIds.length > 0) {
      const inserts = participantIds.map(uid => ({ meeting_id: id, user_id: uid }));
      await supabase.from('meeting_participants').insert(inserts);
    }
  }
}

export async function deleteDoc(docRef: any) {
  const { path, id } = docRef;
  if (path === 'users') {
    try {
      await fetch('/api/members/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: id })
      });
    } catch (e) {
      console.warn("API delete notice:", e);
    }
    await supabase.from('member_subscriptions').delete().eq('user_id', id);
  }
  if (path === 'future_presentations') {
    try {
      const localRaw = localStorage.getItem('ssk_future_presentations_fallback');
      if (localRaw) {
        const localItems: any[] = JSON.parse(localRaw);
        const filtered = localItems.filter(item => String(item.id) !== String(id));
        localStorage.setItem('ssk_future_presentations_fallback', JSON.stringify(filtered));
      }
    } catch (e) {}
  }
  const { error } = await supabase.from(path).delete().eq('id', id);
  if (error) console.error("deleteDoc error:", error);
}

export function onSnapshot(queryRef: any, callback: (snapshot: any) => void) {
  const fetchAndCallback = () => {
    getDocs(queryRef).then(callback).catch(err => {
      console.warn("onSnapshot fetch error:", err);
      callback({ docs: [], empty: true, forEach: () => {} });
    });
  };
  
  fetchAndCallback();

  const tableName = queryRef.path || (queryRef.type === 'collection' ? queryRef.path : null);
  let channel: any = null;
  if (tableName && typeof tableName === 'string') {
    channel = supabase
      .channel(`realtime-snapshot-${tableName}-${Math.random().toString(36).substring(2, 7)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: tableName }, () => {
        fetchAndCallback();
      })
      .subscribe();
  }
  
  window.addEventListener('dashboard-refresh', fetchAndCallback);
  
  return () => {
    if (channel) {
      supabase.removeChannel(channel);
    }
    window.removeEventListener('dashboard-refresh', fetchAndCallback);
  };
}

export function writeBatch(db?: any) {
  const ops: (() => Promise<void>)[] = [];
  return {
    set: (docRef: any, data: any) => ops.push(() => setDoc(docRef, data)),
    update: (docRef: any, data: any) => ops.push(() => updateDoc(docRef, data)),
    delete: (docRef: any) => ops.push(() => deleteDoc(docRef)),
    commit: async () => {
      for (const op of ops) await op();
    }
  };
}

export const serverTimestamp = () => new Date().toISOString();
export const db = {};

export async function updateMemberPositionDirectly(
  targetUserId: string,
  newPosition: string,
  chapterId: string,
  callerId?: string
) {
  const normalizedInput = String(newPosition).toLowerCase().trim().replace(/[\s-]/g, '_');

  let chapterPosVal = 'MEMBER';
  let posVal = 'member';
  let roleVal = 'MEMBER';

  if (normalizedInput === 'chapter_admin') {
    chapterPosVal = 'CHAPTER_ADMIN';
    posVal = 'chapter_admin';
    roleVal = 'CHAPTER_ADMIN';
  } else if (normalizedInput === 'president') {
    chapterPosVal = 'PRESIDENT';
    posVal = 'president';
    roleVal = 'MEMBER';
  } else if (normalizedInput === 'vice_president' || normalizedInput === 'vice-president') {
    chapterPosVal = 'VICE_PRESIDENT';
    posVal = 'vice_president';
    roleVal = 'MEMBER';
  } else if (normalizedInput === 'treasurer') {
    chapterPosVal = 'TREASURER';
    posVal = 'treasurer';
    roleVal = 'MEMBER';
  } else {
    chapterPosVal = 'MEMBER';
    posVal = 'member';
    roleVal = 'MEMBER';
  }

  const resolveMemberPosKey = (m: any): string => {
    if (!m) return 'member';
    const p = String(m.position || '').toLowerCase().trim().replace(/[\s-]/g, '_');
    const c = String(m.chapter_position || '').toLowerCase().trim().replace(/[\s-]/g, '_');
    const r = String(m.role || '').toLowerCase().trim().replace(/[\s-]/g, '_');

    if (p === 'chapter_admin' || c === 'chapter_admin' || r === 'chapter_admin') {
      return 'chapter_admin';
    }
    if (p === 'president' || c === 'president') {
      return 'president';
    }
    if (p === 'vice_president' || c === 'vice_president') {
      return 'vice_president';
    }
    if (p === 'treasurer' || c === 'treasurer') {
      return 'treasurer';
    }
    return 'member';
  };

  // 1. Fetch target user
  const { data: targetUser, error: targetErr } = await supabase
    .from('users')
    .select('id, chapter_id, role, position, chapter_position, name')
    .eq('id', targetUserId)
    .single();

  if (targetErr || !targetUser) {
    throw new Error("Target member not found");
  }

  const targetChapterId = chapterId || targetUser.chapter_id;

  if (targetUser.role === 'MASTER_ADMIN') {
    roleVal = 'MASTER_ADMIN';
  }

  // 2. ONE POSITION PER CHAPTER RULE
  if (posVal !== 'member' && targetChapterId) {
    const { data: chapterMembers } = await supabase
      .from('users')
      .select('id, role, position, chapter_position, name')
      .eq('chapter_id', targetChapterId);

    if (chapterMembers && chapterMembers.length > 0) {
      for (const member of chapterMembers) {
        if (member.id !== targetUserId) {
          const currentMemberPosKey = resolveMemberPosKey(member);

          if (currentMemberPosKey === posVal) {
            // Reassign existing holder of this position in this chapter to regular 'MEMBER'
            const demoteRole = member.role === 'MASTER_ADMIN' ? 'MASTER_ADMIN' : 'MEMBER';
            const { error: demoteErr } = await supabase
              .from('users')
              .update({
                role: demoteRole,
                chapter_position: 'MEMBER',
                position: 'member'
              })
              .eq('id', member.id);

            if (demoteErr) {
              console.error("Failed to demote existing position holder:", demoteErr);
              throw new Error("Failed to demote existing position holder: " + demoteErr.message);
            }
          }
        }
      }
    }
  }

  // 3. Assign target user to new position/role
  const { error: updateErr } = await supabase
    .from('users')
    .update({ 
      role: roleVal, 
      chapter_position: chapterPosVal,
      position: posVal
    })
    .eq('id', targetUserId);

  if (updateErr) {
    console.error("Failed to update target user position:", updateErr);
    throw new Error(updateErr.message || "Failed to update member position");
  }

  // 4. Keep chapters table leadership IDs in sync
  if (targetChapterId) {
    const { data: updatedChapterUsers } = await supabase
      .from('users')
      .select('id, role, position, chapter_position')
      .eq('chapter_id', targetChapterId);

    if (updatedChapterUsers) {
      const findIdForPos = (targetKey: string) => {
        const u = updatedChapterUsers.find(m => resolveMemberPosKey(m) === targetKey);
        return u ? u.id : null;
      };

      await supabase.from('chapters').update({
        chapter_admin_id: findIdForPos('chapter_admin'),
        president_id: findIdForPos('president'),
        vice_president_id: findIdForPos('vice_president'),
        treasurer_id: findIdForPos('treasurer')
      }).eq('id', targetChapterId);
    }
  }

  return { success: true, message: "Position updated successfully" };
}


